import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import useSWR from "swr";
import { useRoutePath } from "../../../../../util/Routing";
import type { UUID } from "../../../../../types/types";
import type { AttributeTemplateSummarySchema } from "../../../../../types/types.PIM";
import { NavBarTabs } from "../../../../../layout/shared/Nav/NavBar";
import { useStoreState } from "../../../../../util/util";
import { useMdmEntityDisplayNames } from "../../../../../util/mdmEntityDisplayNames";
import styled from "styled-components";

enum PIMTemplateClassification {
  GROUPS,
  COLLECTIONS,
  ASSETS,
  ACTIVITY,
  VIEW,
  SETTINGS,
}
enum PIMTemplateClassificationWithForms {
  GROUPS,
  COLLECTIONS,
  ASSETS,
  ACTIVITY,
  VIEW,
  FORMS,
  SETTINGS,
}

export const TemplatesNavWrapper = styled.div`
  margin-bottom: ${({ theme }) => theme.resolve("spacing_3xl")};
`;

export const TemplatesNavWithPadding = styled(TemplatesNavWrapper)`
  .nav-bar-tabs-header {
    padding: ${({ theme }) => `0 ${theme.resolve("spacing_xl")}`};
  }
  .nav-bar-tabs [data-reach-tabs] [role="tablist"] {
    padding: ${({ theme }) => `0 ${theme.resolve("spacing_lg")}`};
  }
  margin-bottom: 0;
`;

type TabEntry = { label: string; path: string };

/**
 * Potential Use case:
 * <PageWrapper>
    <AttributesNav tabIndex={0} />
      {other components come here}
    </PageWrapper>
 * @param param0 
 * @returns 
 */

export const TemplatesNav = ({
  tabIndex,
  templateID,
  pageTitle,
  enableCustomForms = false,
}: {
  tabIndex?: PIMTemplateClassification | PIMTemplateClassificationWithForms;
  templateID: UUID;
  pageTitle: string;
  enableCustomForms?: boolean;
}) => {
  const { adminPath } = useRoutePath();
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { tenant_id } = useStoreState();
  const { getTemplateDisplayName } = useMdmEntityDisplayNames();
  const {
    storefront_metadata: { enable_product_creation_via_form },
  } = useStoreState();
  const enabledFormsTab = enable_product_creation_via_form && enableCustomForms;

  const { data: template } = useSWR<AttributeTemplateSummarySchema>(
    `/v2/tenants/${tenant_id}/pim/templates/${templateID}`
  );

  const resolvedPageTitle = template
    ? getTemplateDisplayName(template, pageTitle)
    : pageTitle;

  const isProductTemplate =
    !template?.template_type || template.template_type === "product";

  const tabEntries: TabEntry[] = [
    { label: t("Groups"), path: "groups" },
    { label: t("Collections"), path: "collections" },
    ...(isProductTemplate ? [{ label: t("Assets"), path: "assets" }] : []),
    { label: t("Activity"), path: "activity" },
    { label: t("View"), path: "view" },
    ...(enabledFormsTab ? [{ label: t("Forms"), path: "forms" }] : []),
    ...[{ label: t("Settings"), path: "settings" }],
  ];

  // Derive active tab index from the current URL path.
  // The tab segment comes right after the template ID in the path:
  // .../templates/:template_id/<tab_segment>/...
  const pathSegments = location.pathname.split("/");
  const templateIdIndex = pathSegments.indexOf(templateID);
  const tabSegment =
    templateIdIndex !== -1 ? pathSegments[templateIdIndex + 1] : undefined;
  const matchedIndex = tabEntries.findIndex(
    (entry) => entry.path === tabSegment
  );
  const activeTabIndex = matchedIndex !== -1 ? matchedIndex : tabIndex ?? 0;

  const handleTabsChange = (index: number) => {
    history.push({
      pathname: `${adminPath}/pim/templates/${templateID}/${tabEntries[index].path}`,
      state: location.state, // Preserve the state when switching tabs
    });
  };
  const tabs = tabEntries.map((entry) => entry.label);
  return (
    <NavBarTabs
      pageTitle={resolvedPageTitle}
      handleTabsChange={handleTabsChange}
      tabs={tabs}
      tabIndex={activeTabIndex}
    />
  );
};
